/**
 * Mobile Viewport Height Utilities
 * 
 * This utility handles the mobile viewport height issue where 100vh doesn't account
 * for dynamic browser chrome (address bar, navigation buttons) on mobile devices.
 * 
 * The solution uses CSS custom properties that are dynamically updated by JavaScript
 * to provide accurate viewport dimensions.
 */

// Function to calculate and set the actual viewport height
function setViewportHeight() {
  // Get the actual viewport height (without browser chrome)
  const vh = window.innerHeight * 0.01;
  
  // Set the CSS custom property
  document.documentElement.style.setProperty('--vh-safe', `${vh}px`);
  
  // Calculate app height (full viewport minus mobile header if present)
  const mobileHeaderHeight = 72; // Height of mobile header in pixels
  const isMobile = window.innerWidth < 768;
  const appHeight = isMobile 
    ? window.innerHeight - mobileHeaderHeight 
    : window.innerHeight;
  
  document.documentElement.style.setProperty('--app-height', `${appHeight}px`);
  document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
}

// Initialize viewport height on DOM load
function initViewportHeight() {
  // Set initial height
  setViewportHeight();
  
  // Update on resize (handles orientation changes and browser chrome changes)
  window.addEventListener('resize', setViewportHeight);
  
  // Handle orientation changes (mobile devices) - using orientationchange event
  window.addEventListener('orientationchange', () => {
    // Small delay to ensure browser has updated dimensions
    setTimeout(setViewportHeight, 100);
  });
  
  // Handle scroll events on mobile to detect browser chrome changes
  let ticking = false;
  function handleScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        setViewportHeight();
        ticking = false;
      });
      ticking = true;
    }
  }
  
  // Only add scroll listener on mobile devices
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 150);
    }, { passive: true });
  }
  
  // Handle visibility changes (when switching tabs/apps)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      setTimeout(setViewportHeight, 100);
    }
  });
  
  console.log('✅ Mobile viewport height utilities initialized');
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initViewportHeight);
} else {
  // DOM already loaded
  initViewportHeight();
}

export { setViewportHeight, initViewportHeight };